import { t } from "@lingui/macro";
import {
  type ExpenseBudgetCategory,
  type ProjectManagerOptionsForUsersQuery,
  type ProjectReportOptionsQuery,
  ProjectRevenueDetailDocument,
  type ProjectRevenueDetailQuery,
  type ProjectRevenueDetailQueryVariables,
  ProjectRevenueOrderByColumnEnum,
  type ProjectRevenueQueryArgs,
  ProjectRevenueWhereColumn,
  type ProjectRevenueWhereConditions,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import type { CalendarAction } from "@src/components/ui-kit/datePicker/CalendarActions";
import { ProjectCategorySelectBottomExtraContent } from "@src/components/ui-kit/ProjectCategorySelect";
import { client } from "@src/services/client";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { getBooleanFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import map from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange } from "@src/utils/types";
import { getParamFromQuery } from "@src/utils/url";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import { clone } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { now } from "mobx-utils";
import type { Query } from "nextjs-routes";
import { EMPTY_SUMMARY } from ".";
import { type ProjectReportBaseModel, ProjectReportDetail } from "./models";
import { type CategoryName, type DetailSummary, QuickFilter } from "./types";

type Detail = Map<CategoryName, ProjectReportDetail>;

export class ProjectReportStore {
  pagination: PaginationState;
  where: Filters<ProjectRevenueWhereColumn | "exclude_presale">;
  @observable searchTerm = "";
  @observable dateRange: DateRange = {
    start: startOfMonth(now()),
    end: endOfMonth(now()),
  };

  @observable selectedDetails: Detail = new Map();
  @observable selectedDetailGrossIncome = 0;
  @observable collapsedRows: Set<CategoryName> = new Set();
  @observable fetchingProjectDetail = false;
  @observable isDetailModalOpen = false;
  @observable expenseBudgetCategories: NonNullable<
    Pick<ExpenseBudgetCategory, "id" | "name">
  >[] = [];

  constructor(key: string) {
    makeObservable(this);
    this.pagination = new PaginationState(key);
    this.where = new Filters<ProjectRevenueWhereColumn | "exclude_presale">(
      [
        new Filter({
          column: ProjectRevenueWhereColumn.ClientId,
          operator: SqlOperator.In,
          title: t`Client`,
          options: [],
        }),
        new Filter({
          column: ProjectRevenueWhereColumn.BrandId,
          operator: SqlOperator.In,
          title: t`Brand`,
          options: [],
          sectioned: true,
          hasSelectAllOption: true,
        }),
        new Filter({
          column: ProjectRevenueWhereColumn.TeamId,
          operator: SqlOperator.In,
          title: t`Team`,
          options: [],
        }),
        new Filter({
          column: ProjectRevenueWhereColumn.ProjectManagerId,
          operator: SqlOperator.In,
          title: t`Project Manager`,
          options: [],
          hasSelectAllOption: true,
        }),
        new Filter({
          column: ProjectRevenueWhereColumn.CategoryId,
          operator: SqlOperator.In,
          title: t`Project category`,
          options: [],
          bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
        }),
        new Filter({
          title: t`Exclude presale`,
          column: "exclude_presale",
          operator: SqlOperator.Eq,
          options: getBooleanFilterOptions(),
          value: [],
          includeInGql: false,
        }),
        new Filter({
          column: ProjectRevenueWhereColumn.Status,
          operator: SqlOperator.In,
          title: t`Status`,
          options: [
            {
              label: t`active`,
              value: QuickFilter.Active,
            },
            {
              label: t`finished`,
              value: QuickFilter.Finished,
            },
            {
              label: t`archived`,
              value: QuickFilter.Archived,
            },
          ],
        }),
      ],
      {
        onChange: this.pagination.resetPage,
        tableKey: key,
        orderBy: this.orderBy,
      },
    );
  }

  orderBy = new OrderBy<ProjectRevenueOrderByColumnEnum>([
    {
      column: ProjectRevenueOrderByColumnEnum.FinishedAt,
      order: SortOrder.Desc,
    },
  ]);

  @action.bound initFilters(query: Query | Partial<Query>) {
    this.searchTerm = getParamFromQuery("search", query) ?? "";
    this.where.fromURLSearchParam(query);
    this.orderBy.fromURLSearchParam(query);
    this.pagination.fromURLSearchParam(query);

    if (query.clientId) {
      this.where.filtersByColumn
        .get(ProjectRevenueWhereColumn.ClientId)
        ?.setValue(query.clientId);
    }

    if (query.brandId) {
      this.where.filtersByColumn
        .get(ProjectRevenueWhereColumn.BrandId)
        ?.setValue(query.brandId);
    }

    if (query.from && query.to) {
      this.setDateRange({
        start: Array.isArray(query.from)
          ? new Date(query.from[0])
          : new Date(query.from),
        end: Array.isArray(query.to)
          ? new Date(query.to[0])
          : new Date(query.to),
      });
    }
  }

  @action.bound openDetailModal(
    projectId: ProjectReportBaseModel["id"],
    grossIncome: ProjectReportBaseModel["gross_income_for_date_range"],
  ): void {
    this.isDetailModalOpen = true;
    this.fetchProjectDetail(projectId);
    this.selectedDetailGrossIncome = grossIncome;
  }

  @action.bound closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedDetails.clear();
    this.collapsedRows.clear();
    this.selectedDetailGrossIncome = 0;
  }

  @computed get filters(): ProjectRevenueQueryArgs {
    const excludePresaleFilterValue =
      this.where.filtersByColumn.get("exclude_presale")?.value ?? [];

    return {
      // Exclude presale is not being sent to API as filter, but as a separate field
      where: this.queryVariables.filters.where as ProjectRevenueWhereConditions,
      search: this.queryVariables.filters.search,
      orderBy: this.queryVariables.filters.orderBy,
      from: this.dateRange.start,
      to: this.dateRange.end,
      page: this.queryVariables.page,
      first: this.queryVariables.first,
      exclude_presale:
        excludePresaleFilterValue.length > 0
          ? excludePresaleFilterValue[0] === "true"
          : false,
    };
  }

  @computed get selectedDetailSummary(): DetailSummary {
    const summary = clone(EMPTY_SUMMARY);
    if (this.selectedDetails.size <= 0) return summary;

    this.selectedDetails.forEach((row) => {
      summary.netMargin += row.summary.netMargin;
      summary.netProfit += row.summary.netProfit;
      summary.billings += row.summary.billings;
    });

    summary.netMargin /= Array.from(this.selectedDetails.values()).reduce(
      (prev, curr) => {
        if (curr.summary.netMargin === 0) return prev;
        return prev + 1;
      },
      0,
    );

    return summary;
  }

  @action.bound toggleCollapseAll(): void {
    if (this.collapsedRows.size > 0) {
      this.collapsedRows.clear();
    } else {
      this.selectedDetails?.forEach((_, key) => {
        this.collapsedRows.add(key);
      });
    }
  }

  @action.bound handleCollapse(categoryName: CategoryName): void {
    if (this.collapsedRows.has(categoryName)) {
      this.collapsedRows.delete(categoryName);
    } else {
      this.collapsedRows.add(categoryName);
    }
  }

  @computed get renderableDetails() {
    const details: Array<[CategoryName, ProjectReportDetail]> = [];
    if (!this.selectedDetails) return details;

    this.selectedDetails.forEach((detail, key) => {
      details.push([key, detail]);
    });

    return details;
  }

  @action.bound setDateRange(newRange: DateRange) {
    this.dateRange = newRange;
  }

  @computed get queryVariables() {
    return commonQueryVariables(this);
  }

  @computed get uriParams() {
    return { ...commonSearchParams(this), from: Date, to: Date };
  }

  @computed get periodPickerActions(): Array<CalendarAction> {
    const today = new Date(now());
    return [
      {
        title: t`This month`,
        getDates: () => {
          return {
            start: startOfMonth(today),
            end: endOfMonth(today),
          };
        },
      },
      {
        title: t`Last month`,
        getDates: () => {
          return {
            start: startOfMonth(subMonths(today, 1)),
            end: endOfMonth(subMonths(today, 1)),
          };
        },
      },
      {
        title: t`This year`,
        getDates: () => {
          return {
            start: startOfYear(today),
            end: endOfYear(today),
          };
        },
      },
      {
        title: t`Last year`,
        getDates: () => {
          return {
            start: startOfYear(subYears(today, 1)),
            end: endOfYear(subYears(today, 1)),
          };
        },
      },
    ];
  }

  @action setTableOptions(query: ProjectReportOptionsQuery) {
    this.where.filtersByColumn
      .get(ProjectRevenueWhereColumn.BrandId)
      ?.setOptions(map.clients.toOptions(query.clientsSimpleMap));

    this.where.filtersByColumn
      .get(ProjectRevenueWhereColumn.TeamId)
      ?.setOptions(map.toOptions(query.teamSimpleMap, "name"));

    this.where.filtersByColumn
      .get(ProjectRevenueWhereColumn.ClientId)
      ?.setOptions(
        query.clientsSimpleMap.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );
  }

  @action setTableAdditionalOptions(query: ProjectManagerOptionsForUsersQuery) {
    this.where.filtersByColumn
      .get(ProjectRevenueWhereColumn.ProjectManagerId)
      ?.setOptions(
        query.projectManagerSimpleMap.map((projectManager) => ({
          value: projectManager?.id,
          label: `${projectManager?.full_name} (${projectManager?.codename})`,
        })),
      );
  }

  @action.bound async fetchProjectDetail(
    projectId: ProjectReportBaseModel["id"],
  ) {
    this.fetchingProjectDetail = true;

    const { data, error } = await client.query<
      ProjectRevenueDetailQuery,
      ProjectRevenueDetailQueryVariables
    >(ProjectRevenueDetailDocument, {
      projectId: projectId,
      from: this.dateRange.start,
      to: this.dateRange.end,
    });

    this.fetchingProjectDetail = false;

    if (error || !data) return;

    if (data.projectRevenueDetail.ourWork.length > 0) {
      this.selectedDetails.set(
        t`Our work`,
        new ProjectReportDetail(data.projectRevenueDetail.ourWork),
      );
    }

    const categoryCount = this.expenseBudgetCategories.length;

    for (let i = 0; i < categoryCount; i++) {
      const category = this.expenseBudgetCategories[i];
      const matchingExpense = data.projectRevenueDetail.expensesStructured.find(
        (expense) => expense.expenseBudgetCategory.id === category.id,
      );

      if (!matchingExpense) continue;
      const { budgetItems, expenseBudgetCategory } = matchingExpense;

      this.selectedDetails.set(
        expenseBudgetCategory.name,
        new ProjectReportDetail(budgetItems),
      );
    }
  }

  @action setExpenseBudgetCategories(
    expenseBudgetCategories: Pick<ExpenseBudgetCategory, "id" | "name">[],
  ) {
    this.expenseBudgetCategories = expenseBudgetCategories;
  }
}
